import { z } from 'zod';
import { CALIDADES_LEAD, DETECCIONES_FUENTE, DIRECCIONES_MENSAJE, ENVIADO_POR, ESTADOS_LEAD, FUENTES_LEAD, MOTIVOS_BASURA, MOTIVOS_CIERRE, TIPOS_CAMPANA, TIPOS_MENSAJE, } from '../enums.js';
import { sugerenciaPublicSchema } from './sugerencia.js';
// ---------------------------------------------------------------------------
// Lead — la persona que escribió al WhatsApp de la campaña. Ver SPEC §6.
// ---------------------------------------------------------------------------
export const ETAPAS = [1, 2, 3, 4, 5];
export const etapaSchema = z.number().int().min(1).max(5);
export const ultimoMensajeSchema = z.object({
    texto: z.string().nullable(),
    direccion: z.enum(DIRECCIONES_MENSAJE),
    tipo: z.enum(TIPOS_MENSAJE),
    timestamp: z.string(),
});
export const leadPublicSchema = z.object({
    id: z.string(),
    jid: z.string(),
    telefono: z.string().nullable(),
    nombre: z.string().nullable(),
    // El número de Metamorfosis por el que se le responde siempre (Ciclo 1b, decisión 2).
    instanciaPreferida: z.string(),
    // Los números por los que hay mensajes con esta persona (la preferida va primero).
    instancias: z.array(z.string()),
    fuente: z.enum(FUENTES_LEAD),
    deteccionFuente: z.enum(DETECCIONES_FUENTE),
    campana: z.object({ id: z.string(), nombre: z.string(), tipo: z.enum(TIPOS_CAMPANA) }).nullable(),
    adTitulo: z.string().nullable(),
    etapa: etapaSchema,
    estado: z.enum(ESTADOS_LEAD),
    noLeidos: z.number().int().min(0),
    ultimoMensaje: ultimoMensajeSchema.nullable(),
    ultimoEntranteEn: z.string().nullable(),
    ultimoSalienteEn: z.string().nullable(),
    cerradoEn: z.string().nullable(),
    motivoCierre: z.enum(MOTIVOS_CIERRE).nullable(),
    // Eliminación (motivo obligatorio, borrado lógico — ver ESTADOS_LEAD.BLOQUEADO).
    bloqueadoEn: z.string().nullable(),
    motivoBloqueo: z.string().nullable(),
    // ciclo-calidad-leads: si valía la pena (aparte del estado). El motivo solo
    // existe cuando es BASURA.
    calidad: z.enum(CALIDADES_LEAD),
    motivoBasura: z.enum(MOTIVOS_BASURA).nullable(),
    creadoEn: z.string(),
    // Ciclo 2: la sugerencia sin consumir más reciente de este lead, si hay una.
    sugerenciaPendiente: sugerenciaPublicSchema.nullable(),
    // Ciclo 2 (botón "generar ahora", 29/09): si hay una ventana de agrupación
    // corriendo (mensaje entrante reciente, todavía sin sugerencia), esta es la
    // hora en que vence sola. La bandeja la usa para mostrar el botón que la
    // salta.
    ventanaSugerenciaVenceEn: z.string().nullable(),
});
// Filtros de la lista de la bandeja (pestañas de la izquierda).
export const FILTROS_BANDEJA = [
    'todos',
    'nuevos',
    'activos',
    'esperando',
    'recontactados',
    'cerrados',
    'historial',
    'bloqueados',
];
// Eliminar un lead (borrado lógico): motivo obligatorio para que quede
// registrado por qué (p. ej. contenido inapropiado) — nunca más se le escribe.
export const leadBloquearSchema = z.object({
    motivo: z.string().trim().min(3, 'Contá brevemente el motivo').max(500),
});
export const leadsListQuerySchema = z.object({
    filtro: z.enum(FILTROS_BANDEJA).default('todos'),
    buscar: z.string().trim().max(80).optional(),
    // Ciclo 1c: solo los leads sellados con esta campaña (id).
    campana: z.string().trim().min(1).optional(),
    // Filtro por etapa (1-5), independiente de `filtro` (pedido de Javier,
    // 30/09/2026): ver todos los leads de la etapa 3 juntos, sin importar en
    // qué pestaña de estado están.
    etapa: z.coerce.number().int().min(1).max(5).optional(),
    take: z.coerce.number().int().min(1).max(200).default(100),
});
export const leadActualizarSchema = z
    .object({
    etapa: etapaSchema.optional(),
    nombre: z.string().trim().min(1).max(120).optional(),
})
    .refine((v) => v.etapa !== undefined || v.nombre !== undefined, 'Nada para actualizar');
// Cierre manual desde la bandeja. SIN_RESPUESTA lo pone el cron (Ciclo 3) y
// DERIVADO_SALUD el candado (Ciclo 2): no se eligen a mano.
export const MOTIVOS_CIERRE_MANUAL = ['GANADO', 'NO_INTERESADO'];
export const leadCerrarSchema = z.object({
    motivo: z.enum(MOTIVOS_CIERRE_MANUAL),
});
// ciclo-calidad-leads (R2): calificar a mano desde el ícono de la charla. El
// motivo es obligatorio con BASURA y prohibido con el resto. SIN_CALIFICAR
// sirve para deshacer un error.
export const leadCalificarSchema = z
    .object({
    calidad: z.enum(CALIDADES_LEAD),
    motivoBasura: z.enum(MOTIVOS_BASURA).optional(),
})
    .refine((v) => (v.calidad === 'BASURA') === (v.motivoBasura !== undefined), {
    message: 'El motivo va solo (y siempre) cuando el lead es basura',
    path: ['motivoBasura'],
});
// ---------------------------------------------------------------------------
// Mensaje — una burbuja de la conversación.
// ---------------------------------------------------------------------------
export const mensajePublicSchema = z.object({
    id: z.string(),
    leadId: z.string(),
    direccion: z.enum(DIRECCIONES_MENSAJE),
    tipo: z.enum(TIPOS_MENSAJE),
    texto: z.string().nullable(),
    enviadoPor: z.enum(ENVIADO_POR),
    // Por qué número de Metamorfosis pasó este mensaje.
    instancia: z.string(),
    timestamp: z.string(),
    estadoEntrega: z.string().nullable(),
    // Ciclo audio, R2: ruta relativa para pedirle el binario al backend
    // (/mensajes/:id/audio), o null si nunca se descargó o si ya se purgó (R4).
    audioUrl: z.string().nullable(),
    // ciclo-historial-fiel: editado o borrado en WhatsApp después de mandarlo.
    editadoEn: z.string().nullable(),
    textoOriginal: z.string().nullable(),
    eliminadoEn: z.string().nullable(),
});
export const enviarMensajeSchema = z.object({
    texto: z.string().trim().min(1, 'El mensaje no puede estar vacío').max(4000),
    // Por defecto sale por la instancia preferida del lead. Elegir otra es deliberado
    // y no cambia la preferida salvo que se pida (Ciclo 1b, R3).
    instancia: z.string().trim().min(1).optional(),
    fijarComoPreferida: z.boolean().optional(),
});
// Ciclo audio, R3: metadata del envío de audio grabado — llega junto al archivo
// en el multipart, así que son strings (igual que env.ts con COOKIE_SECURE).
export const enviarAudioMetaSchema = z.object({
    instancia: z.string().trim().min(1).optional(),
    fijarComoPreferida: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
});
// Evento que empuja el backend por SSE cuando cambia un lead (nuevo mensaje,
// envío, cierre). El frontend invalida las queries del lead y de la lista.
export const eventoLeadSchema = z.object({
    tipo: z.literal('lead.actualizado'),
    leadId: z.string(),
    motivo: z.enum(['mensaje_entrante', 'mensaje_saliente', 'estado', 'sugerencia']),
});
// R4 (Ciclo 1b): respaldo = últimas páginas con semántica en vivo; historial = todo,
// los leads nuevos nacen HISTORICO y no cambia el estado de los existentes.
export const sincronizarInputSchema = z.object({
    modo: z.enum(['respaldo', 'historial']).default('respaldo'),
    instancia: z.string().trim().min(1),
    paginas: z.coerce.number().int().min(1).max(1000).optional(),
    porPagina: z.coerce.number().int().min(10).max(200).default(50),
});
export const sincronizarResultadoSchema = z.object({
    procesados: z.number().int(),
    duplicados: z.number().int(),
    ignorados: z.number().int(),
    // ciclo-historial-fiel: un mensaje que falla va al log y la importación sigue.
    fallidos: z.number().int(),
});
//# sourceMappingURL=lead.js.map